import type { ProjectVersion } from "../types/projectVersion.types";

// Scoping the trail to the revision being viewed (RTLCA-5268).
//
// Revisions are BRANCHES and the trail forks with them: every row carries the revision that was
// current when it was recorded (`revisionId`, trigger-stamped provenance), every revision knows
// where it forked from (`parentRevisionId` + `versionNumber`, the branch-local number of the fork
// point), and the numbering view numbers per branch — a revision saved from v1 numbers its first
// change v2, while the branch it forked off keeps counting its own line, so two branches each own
// a v3. What one branch's history LOOKS like is composed here: its own rows, plus the parent
// chain's prefixes capped at each fork.
//
// Two subtleties the caps encode:
//  - walking up the chain, a cap can only shrink: a fork can never see more of its grandparent
//    than its parent could;
//  - an ancestor's LOG LINES are additionally time-bounded at the forking child's birth. A log
//    line anchors at the branch's number without advancing it, so a team change recorded on the
//    parent long after the fork still anchors ≤ the fork number — by anchor alone, an ancestor's
//    afterlife would keep leaking into the fork's inherited history.

export interface RevisionBranchMeta {
  id: string;
  parentRevisionId: string | null;
  /** The fork point: the parent-branch-local number this branch's history continues from. */
  versionNumber: number;
  /** ISO timestamptz — when the branch came into existence. */
  createdAt: string;
}

interface BranchCap {
  number: number;
  /** Epoch ms; ancestor log lines recorded after this stay the ancestor's. Null = unbounded. */
  informationalNotAfter: number | null;
}

/** The viewed branch's visible universe: revision id → cap. Null means "show everything". */
export type TrailScope = Map<string, BranchCap>;

/**
 * Builds the viewed revision's chain caps from the loaded revisions — soft-deleted ones included,
 * because a branch forked from a deleted branch still walks through it. Returns null (show all)
 * when the viewed revision is unknown: revisions not loaded yet, or an environment without the
 * versioning schema.
 */
export const buildTrailScope = (viewedRevisionId: string | null, revisions: RevisionBranchMeta[]): TrailScope | null => {
  if (!viewedRevisionId) return null;
  const byId = new Map(revisions.map((revision) => [revision.id, revision]));
  if (!byId.has(viewedRevisionId)) return null;

  const caps: TrailScope = new Map([[viewedRevisionId, { number: Number.POSITIVE_INFINITY, informationalNotAfter: null }]]);

  let cap = Number.POSITIVE_INFINITY;
  let current = byId.get(viewedRevisionId);
  while (current?.parentRevisionId && !caps.has(current.parentRevisionId)) {
    cap = Math.min(cap, current.versionNumber);
    // A child is born after its parent, so this instant only moves earlier walking up — the
    // prefix each ancestor contributed was frozen the moment its forking child was born.
    const bornAt = Date.parse(current.createdAt);
    caps.set(current.parentRevisionId, {
      number: cap,
      informationalNotAfter: Number.isNaN(bornAt) ? null : bornAt,
    });
    current = byId.get(current.parentRevisionId);
  }

  return caps;
};

/**
 * Whether a trail row belongs to the viewed branch's history.
 *
 * Every unknown errs towards showing: no scope (revisions unloaded / no schema), an unattributed
 * row (predates the revision_id column — it can only be single-line history), an unparseable
 * timestamp. A row attributed to a revision OUTSIDE the chain is the one confident "no": it is a
 * sibling branch's work, the exact rows this scoping exists to keep out of the view.
 */
export const isVersionInRevisionScope = (
  version: Pick<ProjectVersion, "revisionId" | "versionNumber" | "informational" | "createdAt">,
  scope: TrailScope | null
): boolean => {
  if (!scope) return true;
  if (!version.revisionId) return true;

  const cap = scope.get(version.revisionId);
  if (!cap) return false;
  if (version.versionNumber > cap.number) return false;
  if (!version.informational || cap.informationalNotAfter === null) return true;

  const createdTime = Date.parse(version.createdAt);
  if (Number.isNaN(createdTime)) return true;
  return createdTime <= cap.informationalNotAfter;
};

/**
 * Convenience wrapper over {@link isVersionInRevisionScope} — returns the same list when there is
 * no scope. Generic in the row type, but the page applies it to TRAIL rows only, before the
 * exported reports are merged in: reports carry their own revision attribution and keep their own
 * scoping (reportHistoryEntries.ts, reportsScopedToRevision).
 */
export const versionsScopedToRevision = <
  T extends Pick<ProjectVersion, "revisionId" | "versionNumber" | "informational" | "createdAt">,
>(
  versions: T[],
  scope: TrailScope | null
): T[] => (scope ? versions.filter((version) => isVersionInRevisionScope(version, scope)) : versions);
